import { useMutation } from '@apollo/client';
import { some } from 'lodash';
import { useCallback, useContext, useSyncExternalStore } from 'react';
import { AppContext } from '../AppContext';
import { PII_REVEAL_HIDDEN_ROLES, PII_REVEAL_TTL_MS } from '../common/constants';
import { REVEAL_USER_PII } from '../modules/users/graphql/Mutations';

const revealedPiiStore = new Map();
const listeners = new Set();

const buildKey = (page, userId) => `${page || ''}::${userId || ''}`;

const notifyListeners = () => {
  listeners?.forEach((listener) => listener());
};

const subscribeToStore = (listener) => {
  listeners?.add(listener);
  return () => {
    listeners?.delete(listener);
  };
};

const readRevealedPii = (key) => {
  const entry = revealedPiiStore?.get(key);
  if (!entry) {
    return null;
  }
  if (entry?.expiresAt <= Date.now()) {
    return null;
  }
  return entry?.data;
};

const writeRevealedPii = (key, data) => {
  revealedPiiStore?.forEach((entry, storeKey) => {
    if (entry?.expiresAt <= Date.now()) {
      revealedPiiStore?.delete(storeKey);
    }
  });
  const expiresAt = Date.now() + PII_REVEAL_TTL_MS;
  revealedPiiStore?.set(key, { data, expiresAt });
  setTimeout(() => {
    const entry = revealedPiiStore?.get(key);
    if (entry?.expiresAt === expiresAt) {
      revealedPiiStore?.delete(key);
      notifyListeners();
    }
  }, PII_REVEAL_TTL_MS);
  notifyListeners();
};

export const clearRevealedPii = () => {
  revealedPiiStore?.clear();
  notifyListeners();
};

export const clearRevealedPiiForUser = (userId) => {
  if (!userId) {
    return;
  }
  let hasRemoved = false;
  revealedPiiStore?.forEach((value, storeKey) => {
    if (storeKey?.endsWith(`::${userId}`)) {
      revealedPiiStore?.delete(storeKey);
      hasRemoved = true;
    }
  });
  if (hasRemoved) {
    notifyListeners();
  }
};

export const canRevealPii = (currentUser) =>
  !some(PII_REVEAL_HIDDEN_ROLES, (role) => currentUser?.roles?.includes(role));

export const isPiiMaskingWhitelistedEmail = (currentUserEmail, whitelistedEmails) =>
  !!currentUserEmail &&
  !!whitelistedEmails?.includes(currentUserEmail?.trim()?.toLowerCase());

/**
 * @param {string} userId  end user whose PII is masked
 */
const usePiiReveal = ({ userId }) => {
  const {
    state: {
      currentUser,
      piiMaskingWhitelistedEmails,
      isPiiMaskingWhitelistLoaded,
    },
  } = useContext(AppContext);

  // eslint-disable-next-line no-undef
  const page = window?.location?.pathname;
  const key = buildKey(page, userId);
  const revealedPii = useSyncExternalStore(
    subscribeToStore,
    useCallback(() => readRevealedPii(key), [key]),
  );

  const [revealUserPii, { loading: isRevealing }] = useMutation(
    REVEAL_USER_PII,
    {
      fetchPolicy: 'no-cache',
      onError: () => {},
    },
  );

  const isWhitelistedEmail = isPiiMaskingWhitelistedEmail(
    currentUser?.email,
    piiMaskingWhitelistedEmails,
  );

  const isPiiMaskingBypassed = isWhitelistedEmail;
  // wait for the whitelist so whitelisted users never see the eye flash in
  const hasRevealPermission =
    isPiiMaskingWhitelistLoaded &&
    canRevealPii(currentUser) &&
    !isPiiMaskingBypassed;

  const reveal = useCallback(async () => {
    if (!hasRevealPermission || !userId || !page || revealedPii) {
      return;
    }
    const response = await revealUserPii({
      variables: {
        data: {
          page,
          userId,
        },
      },
    });
    const data = response?.data?.revealUserPii?.data;
    if (data) {
      writeRevealedPii(key, data);
    }
  }, [hasRevealPermission, userId, page, revealedPii, key]);

  return {
    hasRevealPermission,
    isPiiMaskingBypassed,
    revealedPii,
    isRevealing,
    reveal,
  };
};

export default usePiiReveal;
